

import { Directionality } from '@angular/cdk/bidi';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  Host,
  Input,
  Optional,
  Output,
  Renderer2,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';

import { zoomBigMotion } from 'mwc/core/animation';
import { MwcConfigKey, MwcConfigService, WithConfig } from 'mwc/core/config';
import { MwcNoAnimationDirective } from 'mwc/core/no-animation';
import { BooleanInput, NgStyleInterface, MwcTSType } from 'mwc/core/types';
import { InputBoolean } from 'mwc/core/util';
import {
  isTooltipEmpty,
  MwcTooltipBaseDirective,
  MwcToolTipComponent,
  MwcTooltipTrigger,
  PropertyMapping
} from 'mwc/tooltip';

const NZ_CONFIG_MODULE_NAME: MwcConfigKey = 'popover';

@Directive({
  selector: '[mwc-popover]',
  exportAs: 'mwcPopover',
  host: {
    '[class.mwc-popover-open]': 'visible'
  }
})
export class MwcPopoverDirective extends MwcTooltipBaseDirective {
  static ngAcceptInputType_mwcPopoverArrowPointAtCenter: BooleanInput;

  readonly _mwcModuleName: MwcConfigKey = NZ_CONFIG_MODULE_NAME;

  @Input('mwcPopoverArrowPointAtCenter') @InputBoolean() override arrowPointAtCenter?: boolean;
  @Input('mwcPopoverTitle') override title?: MwcTSType;
  @Input('mwcPopoverContent') override content?: MwcTSType;
  @Input('mwc-popover') override directiveTitle?: MwcTSType | null;
  @Input('mwcPopoverTrigger') override trigger?: MwcTooltipTrigger = 'hover';
  @Input('mwcPopoverPlacement') override placement?: string | string[] = 'top';
  @Input('mwcPopoverOrigin') override origin?: ElementRef<HTMLElement>;
  @Input('mwcPopoverVisible') override visible?: boolean;
  @Input('mwcPopoverMouseEnterDelay') override mouseEnterDelay?: number;
  @Input('mwcPopoverMouseLeaveDelay') override mouseLeaveDelay?: number;
  @Input('mwcPopoverOverlayClassName') override overlayClassName?: string;
  @Input('mwcPopoverOverlayStyle') override overlayStyle?: NgStyleInterface;

  @Input() @WithConfig() mwcPopoverBackdrop?: boolean = false;

  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('mwcPopoverVisibleChange') override readonly visibleChange = new EventEmitter<boolean>();

  override componentRef: ComponentRef<MwcPopoverComponent> = this.hostView.createComponent(MwcPopoverComponent);

  protected override getProxyPropertyMap(): PropertyMapping {
    return {
      mwcPopoverBackdrop: ['mwcBackdrop', () => this.mwcPopoverBackdrop],
      ...super.getProxyPropertyMap()
    };
  }

  constructor(
    elementRef: ElementRef,
    hostView: ViewContainerRef,
    resolver: ComponentFactoryResolver,
    renderer: Renderer2,
    @Host() @Optional() noAnimation?: MwcNoAnimationDirective,
    mwcConfigService?: MwcConfigService
  ) {
    super(elementRef, hostView, resolver, renderer, noAnimation, mwcConfigService);
  }
}

@Component({
  selector: 'mwc-popover',
  exportAs: 'mwcPopoverComponent',
  animations: [zoomBigMotion],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  template: `
    <ng-template
      #overlay="cdkConnectedOverlay"
      cdkConnectedOverlay
      mwcConnectedOverlay
      [cdkConnectedOverlayHasBackdrop]="hasBackdrop"
      [cdkConnectedOverlayOrigin]="origin"
      [cdkConnectedOverlayPositions]="_positions"
      [cdkConnectedOverlayOpen]="_visible"
      [cdkConnectedOverlayPush]="true"
      [mwcArrowPointAtCenter]="mwcArrowPointAtCenter"
      (overlayOutsideClick)="onClickOutside($event)"
      (detach)="hide()"
      (positionChange)="onPositionChange($event)"
    >
      <div
        class="mwc-popover"
        [class.mwc-popover-rtl]="dir === 'rtl'"
        [ngClass]="_classMap"
        [ngStyle]="mwcOverlayStyle"
        [@.disabled]="!!noAnimation?.mwcNoAnimation"
        [mwcNoAnimation]="noAnimation?.mwcNoAnimation"
        [@zoomBigMotion]="'active'"
      >
        <div class="mwc-popover-content">
          <div class="mwc-popover-arrow">
            <span class="mwc-popover-arrow-content"></span>
          </div>
          <div class="mwc-popover-inner" role="tooltip">
            <div>
              <div class="mwc-popover-title" *ngIf="mwcTitle">
                <ng-container *mwcStringTemplateOutlet="mwcTitle">{{ mwcTitle }}</ng-container>
              </div>
              <div class="mwc-popover-inner-content">
                <ng-container *mwcStringTemplateOutlet="mwcContent">{{ mwcContent }}</ng-container>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-template>
  `
})
export class MwcPopoverComponent extends MwcToolTipComponent {
  override _prefix = 'mwc-popover';

  constructor(
    cdr: ChangeDetectorRef,
    @Optional() directionality: Directionality,
    @Host() @Optional() noAnimation?: MwcNoAnimationDirective
  ) {
    super(cdr, directionality, noAnimation);
  }

  get hasBackdrop(): boolean {
    return this.mwcTrigger === 'click' ? this.mwcBackdrop : false;
  }

  protected override isEmpty(): boolean {
    return isTooltipEmpty(this.mwcTitle) && isTooltipEmpty(this.mwcContent);
  }
}
